import { Component, OnInit, Inject, OnDestroy } from "@angular/core";
import { Platform, Config, AlertController, App, Events } from "ionic-angular";
import { StatusBar } from "@ionic-native/status-bar";
import { SplashScreen } from "@ionic-native/splash-screen";
import { Store } from "@ngrx/store";
import { AppState } from "./models/app.state";
import { TranslateService } from "@ngx-translate/core";
import * as UserActions from "./store/actions/user.actions";
import {
  EnvironmentVariables,
  ENV_VARS
} from "./environment-variables/environment-variables.module";
import { FrontPage } from "../pages/front.page/front.page";
import { AuthService } from "./services/auth.service";
import { Subscription } from "rxjs/Subscription";
import { FhirService } from "./services/fhir.service";
import { PatientListingPage } from "../pages/patient-listing.page/patient-listing.page";
import { SupportActionsAdminPage } from "../pages/support-actions-admin.page/support-actions-admin.page";
import { ModalController } from "ionic-angular/components/modal/modal-controller";
import { ManagePatientModal } from "../modals/manage-patient.modal/manage-patient.modal";
import { IDiaprofilPatient } from "./models/diaprofil-patient";
import { ManualAdminPage } from "../pages/manual-admin.page/manual-admin.page";
import { PrivacyPolicyPage } from "../pages/privacy-policy.page/privacy-policy.page";
import { UserGuidePage } from "../pages/user-guide.page/user-guide.page";
import { AdminService } from "./services/admin.service";
import { UserState } from "./store/reducers/user.reducer";
import { ChangePasswordModal } from "../modals/change-password.modal/change-password.modal";
import { StatisticsPage } from "../pages/statistics.page/statistics.page";
import { HcpUsersPage } from "../pages/hcp-users.page/hcp-users.page";
import { HomePage } from "../pages/home.page/home.page";
import { DataExtractPage } from "../pages/data-extract.page/data-extract.page";
import { AutoLogoutService } from "./services/auto-logout.service";
import { CurrentConsultationsPage } from "../pages/current-consultations.page/current-consultations.page";

@Component({
  templateUrl: "app.html"
})
export class DiabetesApp implements OnInit, OnDestroy {
  rootPage;
  authenticated: boolean = false;
  language: "en-GB" | "da-DK" = "da-DK";
  i18n: { [key: string]: string } = {};
  userSubscription: Subscription;
  userState: UserState = null;
  versionNumber: string;
  buildTimeStamp: string;
  constructor(
    platform: Platform,
    statusBar: StatusBar,
    splashScreen: SplashScreen,
    public app: App,
    public store: Store<AppState>,
    public translate: TranslateService,
    public events: Events,
    public ionicConfig: Config,
    public adminService: AdminService,
    private authService: AuthService,
    private fhirService: FhirService,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
    private autoLogout: AutoLogoutService, // Imported here to be initialized, so should not be removed
    @Inject(ENV_VARS) public envVars: EnvironmentVariables
  ) {
    const isClinicalSuiteUse = window.location.href.includes(
      "/#/clinical-suite"
    );
    const isAdLogin = window.location.href.includes("/#/ad-login");
    this.versionNumber = envVars.VERSION_NUMBER;
    this.buildTimeStamp = envVars.BUILD_TIMESTAMP;
    this.translate.setDefaultLang(this.language);
    platform.ready().then(() => {
      // Okay, so the platform is ready and our plugins are available.
      // Here you can do any higher level native things you might need.
      statusBar.styleDefault();
      splashScreen.hide();
    });
    events.subscribe("token:expired", () => {
      const prompt = this.alertCtrl.create({
        title: this.translate.instant("GENERIC_UI.TOKEN_EXPIRED_ALERT.HEADER"),
        message: this.translate.instant(
          "GENERIC_UI.TOKEN_EXPIRED_ALERT.MESSAGE"
        ),
        buttons: ["OK"]
      });
      prompt.present();
      this.rootPage = FrontPage;
    });
    events.subscribe("trigger:managePatientModal", params => {
      const data = params as {
        prefilled: { ssn: string; name: string };
        showAlert: boolean;
      };
      this.triggerManagePatientModal({}, undefined, data.prefilled).then(
        result => {
          if (data.showAlert) {
            const prompt = this.alertCtrl.create({
              title: "Patient findes ikke i DiaProfil",
              message: `Patienten ${
                data.prefilled.name !== null ? data.prefilled.name : ""
              } med CPR ${
                data.prefilled.ssn
              } findes ikke i DiaProfil. Du kan benytte denne formular til at oprette patienten.`,
              buttons: ["OK"]
            });
            prompt.present();
          }
        }
      );
    });
    if (!isClinicalSuiteUse && !isAdLogin) {
      this.store
        .select(state => state)
        .subscribe(state => {
          if (state.hydrated === true) {
            if (
              this.authService.jwt !== null &&
              state.user.definitionsLoaded === true
            ) {
              const isJwtExpired = this.authService.jwtHelper.isTokenExpired(
                this.authService.jwt
              );
              if (isJwtExpired === true) {
                this.authService.resetAppState(true);
              } else {
                this.rootPage = HomePage;
                if (this.authenticated === false) {
                  this.authService.setOrganizationConfig().subscribe();
                }
                this.authenticated = true;
              }
            } else {
              this.rootPage = FrontPage;
            }
          }
        });
    }
  }

  geti18n() {
    this.translate
      .get([
        "GENERIC_UI.ION_SELECT.OK_TEXT",
        "GENERIC_UI.ION_SELECT.CANCEL_TEXT",
        "MENU.LOGOUT_ALERT.HEADER",
        "MENU.LOGOUT_ALERT.CANCEL_BUTTON",
        "MENU.LOGOUT_ALERT.CONFIRM_BUTTON"
      ])
      .subscribe(translations => {
        this.i18n = translations;
      });
  }

  ngOnInit() {
    this.userSubscription = this.store
      .select(state => state.user)
      .subscribe(user => {
        this.userState = user;
        this.translate.use(user.language).subscribe(result => {
          this.translate.get("GENERIC_UI.BACK_BTN").subscribe(backLabel => {
            this.ionicConfig.set("ios", "backButtonText", backLabel);
          });
          this.language = user.language;
          this.geti18n();
        });
      });
    setTimeout(() => {
      this.adminService.fetchNews();
      this.adminService.fetchServiceMessages();
    }, 1000);
  }

  ngOnDestroy() {
    this.userSubscription.unsubscribe();
  }

  toPatientListingPage() {
    const rootNav = this.app.getRootNav();
    rootNav.push(PatientListingPage);
  }

  triggerManagePatientModal(
    $event,
    patient?: IDiaprofilPatient,
    prefilled?: { ssn: string; name: string }
  ): Promise<any> {
    const navParams =
      patient !== undefined
        ? { patient, modalType: "EDIT" }
        : { modalType: "CREATE", prefilled };
    const modal = this.modalCtrl.create(ManagePatientModal, navParams, {
      cssClass: "large-modal",
      enableBackdropDismiss: false
    });
    return modal.present();
  }

  isStatisticsPageEnabled(): boolean {
    return (
      this.adminService.isFeatureEnabled("ffStatisticsPage") &&
      this.userState.isSuperUser
    );
  }

  toStatisticsPage() {
    const rootNav = this.app.getRootNav();
    rootNav.setRoot(StatisticsPage);
  }

  isHcpUsersPageEnabled(): boolean {
    return (
      this.adminService.isFeatureEnabled("ffHcpUsersPage") &&
      this.userState.isSuperUser
    );
  }

  toHcpUsersPage() {
    const rootNav = this.app.getRootNav();
    rootNav.setRoot(HcpUsersPage);
  }

  toSupportActionsAdminPage() {
    const rootNav = this.app.getRootNav();
    rootNav.setRoot(SupportActionsAdminPage);
  }

  toManualAdminPage() {
    const rootNav = this.app.getRootNav();
    rootNav.setRoot(ManualAdminPage);
  }

  toUserGuidePage() {
    const rootNav = this.app.getRootNav();
    rootNav.push(UserGuidePage);
  }

  toPrivacyPolicyPage() {
    const rootNav = this.app.getRootNav();
    rootNav.push(PrivacyPolicyPage);
  }

  toDataExtractPage() {
    const rootNav = this.app.getRootNav();
    rootNav.push(DataExtractPage);
  }

  toHomePage() {
    const rootNav = this.app.getRootNav();
    rootNav.setRoot(HomePage);
  }

  toCurrentConsultationsPage() {
    const rootNav = this.app.getRootNav();
    rootNav.setRoot(CurrentConsultationsPage);
  }

  handleLogout(event) {
    const prompt = this.alertCtrl.create({
      title: this.i18n[`MENU.LOGOUT_ALERT.HEADER`],
      buttons: [
        {
          text: this.i18n[`MENU.LOGOUT_ALERT.CANCEL_BUTTON`],
          handler: _data => {
            //
          }
        },
        {
          text: this.i18n[`MENU.LOGOUT_ALERT.CONFIRM_BUTTON`],
          handler: _data => {
            this.fhirService.startProcessedResourcesTracking();
            this.authService.logout().subscribe();
          }
        }
      ]
    });
    prompt.present();
  }

  onLanguageChange(event) {
    this.store.dispatch(
      new UserActions.LanguageAction({
        language: this.language
      })
    );
  }

  triggerChangePasswordModal() {
    const navParams = {};
    const modal = this.modalCtrl.create(ChangePasswordModal, navParams, {
      enableBackdropDismiss: false
    });
    modal.present();
  }
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts